import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Button } from './ui/button';
import { User, DollarSign, Wrench, TrendingUp, Phone, AlertTriangle, FileText, Lock, Key } from 'lucide-react';
import SetPasswordModal from './SetPasswordModal';
import SetLoginModal from './SetLoginModal';

const EmployeeCard = ({ employee, authHeader, userRole, onEmployeeUpdate }) => {
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [showSetLoginModal, setShowSetLoginModal] = useState(false);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('uk-UA', { 
      style: 'currency', 
      currency: 'UAH',
      minimumFractionDigits: 0 
    }).format(amount);
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBgColor = (score) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 80) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <>
      <Card className="w-full mb-4 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-gray-900">{employee.name}</CardTitle>
                <p className="text-sm text-gray-600">{employee.position}</p>
                {employee.login && (
                  <p className="text-xs text-gray-500">Логин: {employee.login}</p>
                )}
                <div className="flex flex-wrap gap-2 mt-1">
                  {employee.updatedAt && (
                    <p className="text-xs text-gray-500">
                      Оновлено: {new Date(employee.updatedAt).toLocaleDateString('uk-UA')}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {employee.grade}
              </Badge>
              {userRole === 'admin' && (
                <div className="flex space-x-1">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowSetLoginModal(true)}
                    className="flex items-center space-x-1"
                  >
                    <Key className="w-4 h-4" />
                    <span>Логин</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowSetPasswordModal(true)}
                    className="flex items-center space-x-1"
                  >
                    <Lock className="w-4 h-4" />
                    <span>Пароль</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Зарплата */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <DollarSign className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="font-semibold text-gray-900">Заробітна плата</h3>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
               <div>
                <p className="text-gray-600">Дохід за ТО ліфтів</p>
                <p className="font-semibold">{formatCurrency(employee.salary.payTo)}</p>
              </div>  
               <div>
                <p className="text-gray-600">Пальне/Проїзний</p>
                <p className="font-semibold">{employee.salary.fuel}</p>
              </div>  
              <div className="col-span-2 border-t border-gray-200 my-2"></div>
              <div>
                <p className="text-gray-600">Посадовий оклад</p>
                <p className="font-semibold">{formatCurrency(employee.salary.baseSalary)}</p>
              </div>
              <div>
                <p className="text-gray-600">Доплата за ТО ліфтів</p>
                <p className="font-semibold">{formatCurrency(employee.salary.allowances)}</p>
              </div>
               <div>
                <p className="text-gray-600">Компенсація послуг зв'язку</p>
                <p className="font-semibold">{formatCurrency(employee.salary.kompensaciya)}</p>
              </div>   
              <div>
                {/* <p className="text-gray-600">Інші доплати</p>
                <p className="font-semibold">{formatCurrency(employee.salary.old_allowance)}</p> */}
              </div>  
            </div> 
            <div className="col-span-2 border-t border-gray-200 my-2"></div>
            <h4 className="font-semibold text-gray-600 text-center text-sm w-full">Нарахування на період відпустки(лікарняного)</h4>
            <div className="my-2"></div>

            <div className="grid grid-cols-2 gap-3 text-sm">  
              <div>
                <p className="text-gray-600">Робочі дні</p>
                <p className="font-semibold">{employee.salary.daysWorked}</p>
              </div>
              <div>
                <p className="text-gray-600">Відпустка(лікарняні)</p>
                <p className="font-semibold">{employee.salary.daysOff}</p>
              </div>   
              <div className="col-span-2 border-t border-gray-200 my-2"></div>                                          
              <div>
                <p className="text-gray-600">Премія за повноту виконання робіт з ТО ліфтів.</p>
                <p className="font-semibold text-green-600">{formatCurrency(employee.salary.bonus)}</p>
              </div>                       
              <div>
                <p className="text-gray-600">Штрафи</p>
                <p className="font-semibold text-red-600">-{formatCurrency(employee.salary.fines)}</p>
              </div>                                    
            </div>
            {/* <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-gray-600">Дохід нарахований:</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(employee.salary.netPay)}</p>
              </div>
            </div> */}
          </div>



          {/* Інші доплати - Accordion */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-yellow-600 mr-2" />
                <h3 className="font-semibold text-gray-900">Інші доплати</h3>
              </div>
              <span className="font-semibold text-yellow-700">{formatCurrency(employee.salary.old_allowance)}</span>
            </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="other-allowances" className="border-t border-yellow-200">
                <AccordionTrigger className="text-gray-700 hover:underline py-2">
                  Це включає різні доплати, які не відносяться до основного окладу або доплат за ТО ліфтів.
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm text-gray-700 pt-2">
                    {/* <p className="text-gray-500 italic">
                      Це включає різні доплати, які не відносяться до основного окладу або доплат за ТО ліфтів.
                    </p>
                    <div className="flex justify-between items-center border-b border-yellow-100 pb-2">
                      <span>Загальна сума інших доплат</span>
                      <span className="font-semibold">{formatCurrency(employee.salary.old_allowance)}</span>
                    </div> */}
                    {/* Display notes that contain "ІНШІ ДОПЛАТИ" */}
                    {employee.notes && employee.notes.map((note, index) => {
                      if (note.includes("ІНШІ ДОПЛАТИ")) {
                        // Удалите префикс "ІНШІ ДОПЛАТИ:" из отображаемого текста
                        const cleanNote = note.replace("ІНШІ ДОПЛАТИ:", "").trim();                        
                        return (
                          <div key={index} className="flex items-start mt-2">
                            <FileText className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
                          {cleanNote.includes('\n') ? (
                            <div className="whitespace-pre-line">
                              {cleanNote}
                            </div>
                          ) : (
                            <p>{cleanNote}</p>
                          )}
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>           
          </div>

          {/* Дохід нарахований */}
            {/* <div className="mt-4 pt-3 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <p className="text-gray-600">Дохід нарахований:</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(employee.salary.netPay)}</p>
              </div>
            </div> */}   
          <div className="bg-gray-50 p-4 rounded-lg">        
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <DollarSign className="w-5 h-5 text-green-600 mr-2" />
                <h3 className="font-semibold text-gray-900">Дохід нарахований:</h3>
              </div>
              <span className="text-xl font-bold text-green-700">{formatCurrency(employee.salary.netPay)}</span>
            </div>
          </div> 
          {/* Показники роботи */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <Wrench className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="font-semibold text-gray-900">Показники роботи</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm mb-4">
              <div>
                <p className="text-gray-600">Пасажирські</p>
                <p className="text-lg font-bold text-blue-600">{employee.workMetrics.passengerElevators}</p>
              </div>
              <div>
                <p className="text-gray-600">Комерційні</p>
                <p className="text-lg font-bold text-purple-600">{employee.workMetrics.commercialElevators}</p>
              </div>
                <div>
                    <p className="text-gray-600">Всі ліфти</p>
                    <p className="text-lg font-bold text-green-600">{employee.workMetrics.totalElevators}</p>
                </div>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Всі заявки:</span>
                <span className="font-semibold">{employee.workMetrics.applications.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Повторні заявки:</span>
                <span className="font-semibold text-orange-900">{employee.workMetrics.applications.repeat}</span>
              </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">Застрягання:</span>
                    <span className="font-semibold text-orange-600">{employee.workMetrics.applications.stuck}</span>
                </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Зупинені ліфти:</span>
                <span className="font-semibold text-red-600">{employee.workMetrics.applications.stopped}</span>
              </div>
            </div>
          </div>

          {/* Ефективність */}
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center mb-3">
              <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
              <h3 className="font-semibold text-gray-900">Ефективність</h3>
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="text-gray-600 text-sm">Виконання ТО</span>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-xs text-gray-600">(фото журнала ТО, фото криші кабіни, фото приямка згідно до регламенту ТО)</span>
                  <span className={`font-semibold ${getPerformanceColor(employee.performance.maintenanceCompletion)}`}>
                    {employee.performance.maintenanceCompletion}%
                  </span>
                </div>
                <Progress 
                  value={employee.performance.maintenanceCompletion} 
                  className="h-2"
                />
              </div>
              
              <div>
                <span className="text-gray-600 text-sm">Якість виконання ТО</span>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-xs text-gray-600">(зауваження та робота по усуненню)</span>
                  <span className={`font-semibold ${getPerformanceColor(employee.performance.responseTime)}`}>
                    {employee.performance.responseTime}%
                  </span>
                </div>
                <Progress 
                  value={employee.performance.responseTime} 
                  className="h-2"
                />
              </div>
              
              <div>
                <span className="text-gray-600 text-sm">Реагування та ефективність </span>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-xs text-gray-600">(заявка має бути прийнята механіком протягом 30 хв., фіксація повторних заявок )</span>
                  <span className={`font-semibold ${getPerformanceColor(employee.performance.qualityScore)}`}>
                    {employee.performance.qualityScore}%
                  </span>
                </div>
                <Progress 
                  value={employee.performance.qualityScore} 
                  className="h-2"
                />
              </div>
            </div>
          </div>

          {/* Проблеми */}
          {(employee.workMetrics.maintenance.notCompleted > 0 || 
            employee.workMetrics.maintenance.issuesNotFixed > 0 || 
            employee.workMetrics.maintenance.lateResponse > 0) && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="font-semibold text-red-800">Потребує уваги</h3>
              </div>
              <div className="space-y-1 text-sm text-red-700">
                {employee.workMetrics.maintenance.notCompleted > 0 && (
                  <p>• Не виконано ТО: {employee.workMetrics.maintenance.notCompleted}</p>
                )}
                {employee.workMetrics.maintenance.issuesNotFixed > 0 && (
                  <p>• Неусунуті зауваження: {employee.workMetrics.maintenance.issuesNotFixed}</p>
                )}
                {employee.workMetrics.maintenance.lateResponse > 0 && (
                  <p>• Несвоєчасна реакція: {employee.workMetrics.maintenance.lateResponse}</p>
                )}
              </div>
            </div>
          )}

          {/* Примітки */}
          {employee.notes && employee.notes.length > 0 && (
            <Accordion type="single" collapsible className="w-full bg-gray-50 p-4 rounded-lg">
              <AccordionItem value="notes" className="border-t border-gray-200">
                <AccordionTrigger className="text-gray-900 font-semibold hover:underline">
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-600 mr-2" />
                    Примітки
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 text-sm text-gray-700 pt-2">

                    {employee.notes.map((note, index) => {
                      const cleanNote = note.replace("повторні заявки: ", "").
                      replace("остановлені: ", "").
                      replace("заявки на застрявання: ", "").
                      replace("кількість не зроблених : ", "").
                      trim();
                      return (
                        <div key={index} className="flex items-start">
                          <FileText className="w-4 h-4 text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            {/* Если ІНШІ ДОПЛАТИ */}

                            {/* Если комментарий содержит переносы строк, отображаем их правильно */}
                            {cleanNote.includes('\n') ? (
                              <div className="whitespace-pre-line">
                                {cleanNote}
                              </div>
                            ) : (
                              <p>{cleanNote}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>
      
      {userRole === 'admin' && showSetPasswordModal && (
        <SetPasswordModal
          employee={employee}
          authHeader={authHeader}
          onClose={() => setShowSetPasswordModal(false)}
          onPasswordSet={() => {
            setShowSetPasswordModal(false);
          }}
        />
      )}
      
      {userRole === 'admin' && showSetLoginModal && (
        <SetLoginModal
          employee={employee}
          authHeader={authHeader}
          onClose={() => setShowSetLoginModal(false)}
          onLoginSet={(id, newLogin) => {
            setShowSetLoginModal(false);
            // Notify parent component about the update
            if (onEmployeeUpdate) {
              onEmployeeUpdate(id, { login: newLogin });
            }
          }}
        />
      )}
    </>
  );
};

export default EmployeeCard;